// src/pages/useradmin/dispute/ResolveDisputes.jsx

import React, { useState, useEffect, useRef, useContext } from "react";
import { Row, Col, Form } from "react-bootstrap";
import {
  FaExclamationCircle,
  FaCheckCircle,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import {
  FiMoreVertical,
  FiEye,
  FiTrash2,
  FiX,
  FiCheckCircle,
  FiSend,
} from "react-icons/fi";
import UserMasterLayout from "../../../layout/useradmin/UserMasterLayout.js";
import "../../../assets/css/useradmin/userdispute/UserDispute.css";
import AppPagination from "../../../components/general/pagination/Pagination.js";
import AppContext from "../../../context/AppContext.js";
import axios from "axios";
import Swal from "sweetalert2";

// ── Helpers ──
const getInitials = (name) =>
  (name || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// Who raised the dispute — always the customer
const getRaisedBy = (dispute) => dispute?.srcustomer || {};

// Who the dispute is against — mechanic admin OR rental admin, whichever applies
// NOTE: this already returns the resolved admin object itself
// (e.g. { id, fullname, email, image, ... }) — do NOT nest further
// into `.srrentaladmin` / `.srmechanicadmin` when reading from it.
const getAgainst = (dispute) =>
  dispute?.srmechanicadmin_id
    ? dispute?.srmechanicadmin || {}
    : dispute?.srrentaladmin || {};

const getAgainstLabel = (dispute) =>
  dispute?.srmechanicadmin_id ? "Mechanic Admin" : "Rental Admin";

const UserAdminResolveDisputes = () => {
  const { baseUrl, baseUrlForFile, loggedInCustomer } = useContext(AppContext);

  // ── States ──
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    resolved: 0,
    inProgress: 0,
    urgent: 0,
  });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [forwarding, setForwarding] = useState(false);

  const messagesEndRef = useRef(null);
  const disputesPerPage = 5;
  const totalPages = Math.ceil((disputes?.length || 0) / disputesPerPage);
  const currentDisputes = disputes.slice(
    (currentPage - 1) * disputesPerPage,
    currentPage * disputesPerPage,
  );

  // ── Fetch disputes ──
    const viewMyDisputes = () => {
    if (!loggedInCustomer) return;
    const token = localStorage.getItem("authToken");
    axios
      .post(`${baseUrl}/customer/dispute/viewMyDisputes`, {
        srcustomer_id: loggedInCustomer.id,
      }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        console.log(res.data);
        if (res.data.success) {
          setDisputes(res.data.request || []);
        }
      })
      .catch((err) => console.log("API Error:", err));
  };

  useEffect(() => {
    if (loggedInCustomer?.id) viewMyDisputes();
  }, [loggedInCustomer]);

  // ── Effects ──
  useEffect(() => {
    calculateStats();
  }, [disputes]);

  useEffect(() => {
    if (showModal && messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [conversationMessages, showModal]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".ud-actions-wrapper")) setActiveMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── Functions ──
  const calculateStats = () => {
    const total = disputes.length;
    const resolved = disputes.filter((d) => d.status === "Resolved").length;
    const inProgress = disputes.filter(
      (d) => d.status === "In Progress",
    ).length;
    const urgent = disputes.filter((d) => {
      const daysDiff = Math.floor(
        (new Date() - new Date(d.date)) / (1000 * 60 * 60 * 24),
      );
      return daysDiff > 3 && d.status === "In Progress";
    }).length;
    setStats({ total, resolved, inProgress, urgent });
  };

  // ── Fetch chat/conversation for a dispute (same pattern as Help.jsx -> fetchChatForHelp) ──
  const fetchChatForDispute = (dispute) => {
    const token = localStorage.getItem("authToken");
    axios
      .post(`${baseUrl}/customer/chats/fetchChatForDispute`, {
        srdispute_id: dispute.id,
      }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        console.log("fetchChatForDispute response:", res.data);

        let messages = [];

        if (res.data.success) {
          messages = res.data.chats || [];
          setConversationId(res.data.srconversation_id);
        } else {
          setConversationId(null);
        }

        // ✅ Dispute ki original description ko pehle message ki tarah add karein
        const descriptionMessage = {
          id: `dispute-description-${dispute.id}`,
          sender: "customer",
          message: dispute.description,
          created_at: dispute.date || dispute.created_at,
        };

        setConversationMessages([descriptionMessage, ...messages]);
      })
      .catch((err) => {
        console.log(err);

        // Error ke waqt bhi description to dikhani chahiye
        const descriptionMessage = {
          id: `dispute-description-${dispute.id}`,
          sender: "customer",
          message: dispute.description,
          created_at: dispute.date || dispute.created_at,
        };

        setConversationMessages([descriptionMessage]);
        setConversationId(null);
      });
  };

  const handleOpenDispute = (dispute) => {
    setSelectedDispute(dispute);
    setShowModal(true);
    setNewMessage("");
    setActiveMenu(null);
    fetchChatForDispute(dispute);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDispute(null);
    setNewMessage("");
    setConversationMessages([]);
    setConversationId(null);
  };

  // ── Send a chat message for this dispute (same pattern as Help.jsx -> sendMessage) ──
  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedDispute || !conversationId) return;

     const token = localStorage.getItem("authToken");

    axios
      .post(`${baseUrl}/customer/dispute/sendMessageForDispute`, {
        srconversation_id: conversationId,
        sender: "customer",
        message: newMessage,
      }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        console.log(res.data);
        if (res.data.hasOwnProperty("error")) {
          console.log(res.data.error);
          return;
        }
        setNewMessage("");
        fetchChatForDispute(selectedDispute);
      })
      .catch((err) => console.log(err));
  };

  const handleMarkAsResolved = () => {
    setDisputes(
      disputes.map((d) =>
        d.id === selectedDispute.id ? { ...d, status: "Resolved" } : d,
      ),
    );
    handleCloseModal();
  };

  const handleDeleteDispute = (id) => {
    setActiveMenu(null);
    if (window.confirm("Are you sure you want to delete this dispute?"))
      setDisputes(disputes.filter((d) => d.id !== id));
  };

  // ── Forward dispute to superadmin ──
  const handleForwardToSuperadmin = (dispute) => {
    setActiveMenu(null);

    Swal.fire({
      title: "Are you sure?",
      text: "This dispute will be forwarded to the Superadmin for resolution.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, forward it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#FDB714",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setForwarding(true);
      const token = localStorage.getItem("authToken");

      axios
        .post(
          `${baseUrl}/customer/dispute/forwardToSuperadmin`,
          { srdispute_id: dispute.id },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((res) => {
          setForwarding(false);
          if (res.data.success) {
            setDisputes((prev) =>
              prev.map((d) =>
                d.id === dispute.id
                  ? { ...d, forwarded: true }
                  : d,
              ),
            );
            Swal.fire({
              title: "Forwarded!",
              text: "The dispute has been forwarded to the Superadmin.",
              icon: "success",
              confirmButtonColor: "#FDB714",
            });
          } else {
            Swal.fire({
              title: "Failed",
              text: res.data.message || "Could not forward this dispute.",
              icon: "error",
              confirmButtonColor: "#FDB714",
            });
          }
        })
        .catch((err) => {
          setForwarding(false);
          console.log(err);
          Swal.fire({
            title: "Error",
            text: "Something went wrong while forwarding this dispute.",
            icon: "error",
            confirmButtonColor: "#FDB714",
          });
        });
    });
  };

  const getStatusStyle = (status) => {
    if (status === "resolved") return { bg: "#D1FAE5", color: "#065F46" };

    if (status === "pending") return { bg: "#FEF3C7", color: "#92400E" };

    return { bg: "#E5E7EB", color: "#374151" };
  };

  // Chat message position: the OTHER party (rental admin / mechanic admin)
  // always renders on the left, the logged-in customer always on the right.
  const isCustomerMessage = (msg) => msg.sender === "customer";

  const formatTime = (datetime) => {
    if (!datetime) return "";
    return new Date(datetime).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ════════════════════════════════
  // RETURN
  // ════════════════════════════════
  return (
    <UserMasterLayout>
      <div className="ud-page">
        {/* ── Page Header ── */}
        <div className="ud-page-header">
          <div>
            <h1 className="ud-page-title">Resolve Disputes</h1>
            <p className="ud-page-subtitle">
              View and resolve disputes raised by users
            </p>
          </div>
        </div>

        {/* ════ Stat Cards ════ */}
        <Row className="mb-4">
          {[
            {
              icon: <FaExclamationCircle />,
              value: stats.total,
              label: "Total Disputes",
            },
            {
              icon: <FaCheckCircle />,
              value: stats.resolved,
              label: "Resolved",
            },
            {
              icon: <FaClock />,
              value: stats.inProgress,
              label: "In Progress",
            },
            {
              icon: <FaExclamationCircle />,
              value: stats.urgent,
              label: "Urgent (3 days)",
            },
          ].map((s, i) => (
            <Col lg={3} md={6} className="mb-3" key={i}>
              <div className="ud-stat-card">
                <div className="ud-stat-icon">{s.icon}</div>
                <div className="ud-stat-content">
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ════ Table ════ */}
        <div className="ud-table-section">
          <div className="ud-table-header">
            <h3 className="ud-table-title">User Disputes</h3>
          </div>

          <div className="ud-table-wrapper">
            <table className="ud-table">
              <thead>
                <tr>
                  <th>AGAINST</th>
                  <th>CATEGORY</th>
                  <th>DESCRIPTION</th>
                  <th>DATE</th>
                  <th>STATUS</th>
                  <th>FORWARDED</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentDisputes.length > 0 ? (
                  currentDisputes.map((dispute) => {
                    const st = getStatusStyle(dispute.status);
                    const raisedBy = getRaisedBy(dispute);
                    const against = getAgainst(dispute);
                    const againstLabel = getAgainstLabel(dispute);
                    const isForwarded = !!dispute.forwarded;

                    return (
                      <tr key={dispute.id}>

                        {/* ── AGAINST — reads directly from the resolved
                             `against` object (no extra nesting) ── */}
                        <td>
                          <div className="ud-party-cell">
                            <div className="ud-avatar ud-avatar-against">
                              {against.image ? (
                                <img
                                  src={`${baseUrlForFile}/${against.image}`}
                                  alt={against.fullname || againstLabel}
                                  className="ud-avatar-img"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                getInitials(against.fullname) || "A"
                              )}
                            </div>
                            <div className="ud-party-text">
                              <div className="ud-party-name">
                                {against.fullname || "N/A"}
                              </div>
                              <small className="ud-party-email">
                                {against.email || againstLabel}
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>{dispute.category}</td>
                        <td>
                          <span
                            className="ud-desc-cell"
                            title={dispute.description}
                          >
                            {dispute.description?.length > 40
                              ? dispute.description.substring(0, 40) + "…"
                              : dispute.description || "—"}
                          </span>
                        </td>
                        <td>{new Date(dispute.date).toLocaleDateString()}</td>
                        <td>
                          <span
                            className="ud-status-badge"
                            style={{ background: st.bg, color: st.color }}
                          >
                            {dispute.status}
                          </span>
                        </td>
                        <td>
                          <span
                            className="ud-status-badge"
                            style={
                              isForwarded
                                ? { background: "#DBEAFE", color: "#1E40AF" }
                                : { background: "#E5E7EB", color: "#374151" }
                            }
                          >
                            {isForwarded ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="ud-actions-cell">
                          <div className="ud-actions-wrapper">
                            <button
                              className="ud-menu-btn"
                              onClick={() =>
                                setActiveMenu(
                                  activeMenu === dispute.id ? null : dispute.id,
                                )
                              }
                            >
                              <FiMoreVertical size={20} />
                            </button>
                            {activeMenu === dispute.id && (
                              <div className="ud-dropdown">
                                <button
                                  className="ud-drop-item ud-drop-view"
                                  onClick={() => handleOpenDispute(dispute)}
                                >
                                  <FiEye size={14} /> View Dispute
                                </button>
                                {!isForwarded && (
                                  <button
                                    className="ud-drop-item ud-drop-view"
                                    disabled={forwarding}
                                    onClick={() =>
                                      handleForwardToSuperadmin(dispute)
                                    }
                                  >
                                    <FiSend size={14} /> Forward to Superadmin
                                  </button>
                                )}
                                <button
                                  className="ud-drop-item ud-drop-delete"
                                  onClick={() =>
                                    handleDeleteDispute(dispute.id)
                                  }
                                >
                                  <FiTrash2 size={14} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="ud-empty-row">
                      No disputes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={disputes.length}
            itemsPerPage={disputesPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* ════════════════════════════════
          VIEW DISPUTE MODAL
      ════════════════════════════════ */}
      {showModal && selectedDispute && (
        <div className="ud-overlay" onClick={handleCloseModal}>
          <div className="ud-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="ud-modal-header">
              <div className="ud-modal-header-left">
                <h5>View Dispute</h5>
                <span>Full breakdown of dispute {selectedDispute.id}</span>
              </div>
              <button className="ud-modal-close" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>

            {/* Body */}
            <div className="ud-modal-body">
              {/* Status Bar */}
              <div className="ud-modal-status-bar">
                <div className="ud-sb-left">
                  <span className="ud-sb-id">{selectedDispute.id}</span>
                  <span
                    className="ud-status-badge"
                    style={{
                      background: getStatusStyle(selectedDispute.status).bg,
                      color: getStatusStyle(selectedDispute.status).color,
                    }}
                  >
                    {selectedDispute.status}
                  </span>
                  {selectedDispute.forwarded && (
                    <span
                      className="ud-status-badge"
                      style={{ background: "#DBEAFE", color: "#1E40AF" }}
                    >
                      Forwarded to Superadmin
                    </span>
                  )}
                </div>
                <span className="ud-sb-date">
                  {new Date(selectedDispute.date).toLocaleDateString()}
                </span>
              </div>

              {/* ── Raised By / Against — same visual pattern as the table ── */}
              <p className="ud-section-title">Dispute Parties</p>
              <div className="ud-parties-grid">
                <div className="ud-party-card">
                  <span className="ud-party-card-label">Raised By</span>
                  <div className="ud-party-cell">
                    <div className="ud-avatar">
                      {getRaisedBy(selectedDispute).image ? (
                        <img
                          src={`${baseUrlForFile}/${getRaisedBy(selectedDispute).image}`}
                          alt={getRaisedBy(selectedDispute).fullname || "Customer"}
                          className="ud-avatar-img"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        getInitials(getRaisedBy(selectedDispute).fullname) || "C"
                      )}
                    </div>
                    <div className="ud-party-text">
                      <div className="ud-party-name">
                        {getRaisedBy(selectedDispute).fullname || "N/A"}
                      </div>
                      <small className="ud-party-email">
                        {getRaisedBy(selectedDispute).email || "—"}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="ud-party-card">
                  <span className="ud-party-card-label">
                    Against &bull; {getAgainstLabel(selectedDispute)}
                  </span>
                  <div className="ud-party-cell">
                    <div className="ud-avatar ud-avatar-against">
                      {getAgainst(selectedDispute).image ? (
                        <img
                          src={`${baseUrlForFile}/${getAgainst(selectedDispute).image}`}
                          alt={
                            getAgainst(selectedDispute).fullname ||
                            getAgainstLabel(selectedDispute)
                          }
                          className="ud-avatar-img"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        getInitials(getAgainst(selectedDispute).fullname) || "A"
                      )}
                    </div>
                    <div className="ud-party-text">
                      <div className="ud-party-name">
                        {getAgainst(selectedDispute).fullname || "N/A"}
                      </div>
                      <small className="ud-party-email">
                        {getAgainst(selectedDispute).email || "—"}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <p className="ud-section-title">Dispute Information</p>
              <div className="ud-info-grid">
                <div className="ud-info-item">
                  <label>Dispute ID</label>
                  <span>{selectedDispute.id}</span>
                </div>
                <div className="ud-info-item">
                  <label>Category</label>
                  <span>{selectedDispute.category || "—"}</span>
                </div>
                <div className="ud-info-item">
                  <label>Date Filed</label>
                  <span>
                    {new Date(selectedDispute.date).toLocaleDateString()}
                  </span>
                </div>
                <div className="ud-info-item ud-info-full">
                  <label>Description</label>
                  <span>{selectedDispute.description || "—"}</span>
                </div>
              </div>

              {/* Chat */}
              <p className="ud-section-title">Dispute Resolution Chat</p>
              <div className="ud-chat-box">
                <div className="ud-chat-messages">
                  {conversationMessages?.length > 0 ? (
                    conversationMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`ud-chat-msg ${isCustomerMessage(msg) ? "user" : "admin"}`}
                      >
                        <div
                          className={`ud-msg-bubble ${isCustomerMessage(msg) ? "user" : "admin"}`}
                        >
                          <div className="ud-msg-sender">
                            {isCustomerMessage(msg)
                              ? "You"
                              : msg.sender === "superadmin"
                                ? "Superadmin"
                                : getAgainstLabel(selectedDispute)}
                          </div>
                          <div className="ud-msg-text">{msg.message}</div>
                          <div className="ud-msg-time">
                            {formatTime(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="ud-chat-empty">
                      No messages yet. Start the conversation!
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className="ud-chat-input-area">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="ud-chat-textarea"
                  />
                  <button
                    className="ud-send-btn"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <FaPaperPlane size={14} /> Send
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="ud-modal-footer">
              <button
                className="ud-mf-btn ud-mf-resolve"
                onClick={handleMarkAsResolved}
                disabled={selectedDispute.status === "Resolved"}
              >
                <FiCheckCircle size={14} /> Mark as Resolved
              </button>
              <button
                className="ud-mf-btn ud-mf-close"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </UserMasterLayout>
  );
};

export default UserAdminResolveDisputes;